import { Fragment, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, User, AlertCircle } from 'lucide-react';
import type { MobilisationEvent } from '@hcc/types';
import { useCurrentUser } from '../../hooks/useAuth';
import { Skeleton, SkeletonList } from '../../components/ui/Skeleton';
import { Tabs, TabPanel } from '../../components/ui/Tabs';
import { api, ApiError } from '../../lib/api';
import type { AdminPersonResponse } from './person/types';
import {
  PERSON_TAB_LABELS,
  getVisibleTabs,
  resolveTab,
  type PersonTabId,
} from './person/tabs';
import { SummaryStrip } from './person/SummaryStrip';
import { ProfileTab } from './person/ProfileTab';
import { OnboardingTab } from './person/OnboardingTab';
import { KitTab } from './person/KitTab';
import { HistoryTab } from './person/HistoryTab';
import { AdminTab } from './person/AdminTab';
import { SapTab } from './person/sap/SapTab';

const ALLOWED = ['admin', 'super_admin', 'coordinator', 'hr'];

/**
 * Person record page. Fetches the person + event chain once at this
 * level, then hands the data to five tabbed views (Profile,
 * Onboarding, Kit & Certificates, History & Audit, Admin). The active
 * tab lives in the ?tab= query param so deep links and refresh keep
 * their place. Inactive tabs unmount: panels that seed local state
 * from props lose unsaved drafts on tab switch, the same trade-off
 * the page already makes when the person changes.
 */
export function PersonEdit() {
  const { id } = useParams<{ id: string }>();
  const { data: user, isLoading: meLoading } = useCurrentUser();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<AdminPersonResponse | null>(null);
  const [events, setEvents] = useState<MobilisationEvent[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  async function refreshEvents() {
    if (!id) return;
    try {
      const ev = await api<{ events: MobilisationEvent[] }>(`/api/people/${id}/events`);
      setEvents(ev.events);
    } catch (err) {
      // Surface but don't tank the page; events panel will rerender empty.
      toast.error(err instanceof ApiError ? err.message : 'refresh failed');
    }
  }

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    // Route param changes reuse this mounted component, so clear the
    // previous person before fetching; otherwise the form panels keep
    // person A's state while the URL points at person B and a save
    // would write A's values onto B.
    setData(null);
    setEvents(null);
    setLoadError(null);
    (async () => {
      try {
        const [adminP, ev] = await Promise.all([
          api<AdminPersonResponse>(`/api/admin/people/${id}`),
          api<{ events: MobilisationEvent[] }>(`/api/people/${id}/events`),
        ]);
        if (cancelled) return;
        setData(adminP);
        setEvents(ev.events);
      } catch (err) {
        if (cancelled) return;
        setLoadError(err instanceof ApiError ? err.message : 'load failed');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (meLoading) return <main className="p-6 text-sm text-fg-muted">Loading...</main>;
  if (!user) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Please <Link to="/login" className="underline">sign in</Link>.
      </main>
    );
  }
  if (!user.roles.some((r) => ALLOWED.includes(r))) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        People admin is restricted to admin / coordinator / HR.
      </main>
    );
  }

  const visibleTabs = getVisibleTabs(user.roles);
  const tab = resolveTab(params.get('tab'), visibleTabs);

  function setTab(next: PersonTabId) {
    const nextParams = new URLSearchParams(params);
    // Keep the default URL clean; replace so tab flips don't pollute
    // history and Back still returns to the people list.
    if (next === 'profile') nextParams.delete('tab');
    else nextParams.set('tab', next);
    setParams(nextParams, { replace: true });
  }

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-4xl space-y-6 px-4 py-6 sm:px-6 sm:py-8">
        <Link
          to="/admin/people"
          className="inline-flex items-center gap-1.5 text-sm text-fg-muted transition-colors hover:text-fg"
        >
          <ArrowLeft className="h-4 w-4" />
          All people
        </Link>

        {loadError ? (
          <div className="flex items-start gap-2 rounded-md bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{loadError}</span>
          </div>
        ) : null}

        {!data && !loadError ? (
          <div className="space-y-3">
            <Skeleton className="h-8 w-1/3" />
            <SkeletonList rows={3} height="h-20" />
          </div>
        ) : null}

        {data ? (
          // Keyed so every panel that seeds local state from props
          // remounts if the person ever changes without a null gap.
          <Fragment key={data.person.id}>
            <header className="flex items-start gap-4">
              <div className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-accent/10 text-accent">
                <User className="h-6 w-6" strokeWidth={2} />
              </div>
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-accent">
                  Person record
                </div>
                <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
                  {data.person.full_name}
                </h1>
                <p className="text-sm text-fg-muted">
                  {data.person.position ?? 'Position unknown'}
                  {data.person.email ? ` · ${data.person.email}` : ''}
                </p>
              </div>
            </header>

            <SummaryStrip
              personId={data.person.id}
              metadata={data.metadata}
              checks={data.checks ?? []}
              events={events}
              visibleTabs={visibleTabs}
              onNavigate={setTab}
            />

            <Tabs
              label="Person record sections"
              tabs={visibleTabs.map((t) => ({ id: t, label: PERSON_TAB_LABELS[t] }))}
              active={tab}
              onSelect={(t) => setTab(t as PersonTabId)}
            />

            <TabPanel tabId="profile" active={tab}>
              <ProfileTab
                person={data.person}
                metadata={data.metadata}
                checks={data.checks ?? []}
                onPersonSaved={(p) =>
                  setData((prev) => (prev ? { ...prev, person: p } : prev))
                }
                onMetadataSaved={(meta) =>
                  setData((prev) => (prev ? { ...prev, metadata: meta } : prev))
                }
                refreshEvents={refreshEvents}
              />
            </TabPanel>

            <TabPanel tabId="onboarding" active={tab}>
              <OnboardingTab
                person={data.person}
                metadata={data.metadata}
                events={events}
                refreshEvents={refreshEvents}
              />
            </TabPanel>

            <TabPanel tabId="kit" active={tab}>
              <KitTab person={data.person} events={events} />
            </TabPanel>

            <TabPanel tabId="history" active={tab}>
              <HistoryTab
                personId={data.person.id}
                metadata={data.metadata}
                events={events}
              />
            </TabPanel>

            {visibleTabs.includes('sap') ? (
              <TabPanel tabId="sap" active={tab}>
                <SapTab personId={data.person.id} personName={data.person.full_name} />
              </TabPanel>
            ) : null}

            {visibleTabs.includes('admin') ? (
              <TabPanel tabId="admin" active={tab}>
                <AdminTab
                  person={data.person}
                  events={events}
                  roles={user.roles}
                  refreshEvents={refreshEvents}
                  onGone={() => navigate('/admin/people')}
                />
              </TabPanel>
            ) : null}
          </Fragment>
        ) : !loadError ? (
          <p className="text-sm text-fg-muted">Loading person...</p>
        ) : null}
      </div>
    </main>
  );
}
